import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createLayout } from 'pro-gallery-layouter';
import ddt from '@wix/da-ddt';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { defaultLayoutConfig } from '../../constants';
import { applyMassiveThumbThreshold, applyTinyThumbThreshold, getItemSpacing, getRowSize } from '../helpers';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import performance from '../../../utils/performance';
const logger = ddt.createLogger('torpedo');
export function useTorpedoLayout({ testRecalculateLayoutSpy, items = [], container, layoutConfig: layoutConfigProp, useCardLayout, maxRows, hideIncompleteRows }) {
    const isMobile = useContext(MobileContext);
    const isInitialRun = useRef(true);
    // #region memoize all the things
    // this is to ensure the layout only recalculates when something actually relevant changed
    const layoutConfig = useDeepEqualMemo(_object_spread({}, defaultLayoutConfig, layoutConfigProp));
    var _getRowSize, _getItemSpacing;
    const styleParams = useMemo(()=>({
            isColumnsLayout: false,
            allowedGroupTypes: [
                '1'
            ],
            rowSize: (_getRowSize = getRowSize(layoutConfig, isMobile, useCardLayout)) !== null && _getRowSize !== void 0 ? _getRowSize : 270,
            itemSpacing: (_getItemSpacing = getItemSpacing(layoutConfig, isMobile, useCardLayout)) !== null && _getItemSpacing !== void 0 ? _getItemSpacing : 4
        }), [
        isMobile,
        layoutConfig,
        useCardLayout
    ]);
    // Items get memoized a bit specially. We do not want to recalc layout if the sizes
    // don't change, but we still want to let the content update if it needs to. So we memoize the
    // size and id only here; we will re-construct the items after the layout is calculated
    const layoutedItems = useDeepEqualMemo(// Modify items applying small and massive rules
    items.map(({ id, width, height })=>applyTinyThumbThreshold(applyMassiveThumbThreshold({
            id,
            width,
            height
        } // clone so we dont mess up references
        ), styleParams.rowSize)));
    // #endregion
    // the main layout math. this is expensive, and only re-runs when the container width;
    // the item sizes; or the style params change
    const calculateLayout = useCallback(()=>{
        const t0 = performance.now();
        const newLayout = createLayout({
            items: layoutedItems,
            styleParams,
            container: {
                width: container.width
            }
        });
        const t1 = performance.now();
        logger.log(`created layout (${t1 - t0}ms)`, styleParams, container.width, layoutedItems.length);
        return newLayout;
    }, [
        layoutedItems,
        styleParams,
        container.width
    ]);
    // store the layout in state so updating it triggers a re-render
    const [layout, setLayout] = useState(calculateLayout);
    // slice and dice the calculated layout for special needs
    const rows = useMemo(()=>{
        if (!layout.strips) {
            return [];
        }
        const strips = layout.strips.slice(0, maxRows || undefined)// When `hideIncompleteRows` flag is on, don't render rows that do not
        // fill the entire width, except for the first one.
        // This is to avoid having a last row with a few lone items.
        .filter((strip, index)=>!hideIncompleteRows || strip.isFullWidth || index === 0)// flatten structure and merge back the original items
        .map((strip)=>_object_spread_props(_object_spread({}, strip), {
                items: strip.groups.reduce((flatItems, group)=>[
                        ...flatItems,
                        ...group.items
                    ], [])
            }));
        return strips;
    }, [
        layout.strips,
        hideIncompleteRows,
        maxRows
    ]);
    // this effect basically just updates the state with the new layout whenever the callback rebinds
    // (and that only happens when the above mentioned deps change)
    useEffect(()=>{
        // on initial mount, the state init already calculated our layout, so we can skip that
        if (isInitialRun.current) {
            isInitialRun.current = false;
            return;
        }
        setLayout(calculateLayout());
    }, [
        calculateLayout
    ]);
    // callback for tests...
    // TODO: see if there is a better way to do this
    useEffect(()=>testRecalculateLayoutSpy && testRecalculateLayoutSpy(layout), [
        layout,
        testRecalculateLayoutSpy
    ]);
    return {
        rows,
        styleParams
    };
}
